import { useEffect, useState } from 'react'
import { Markdown } from '../lib/markdown'
import { useMerker } from '../lib/merker'

/**
 * **Lesemodus** ([E-384](../../../../docs/ENTSCHEIDUNGEN.md#e-384), 01.10.2026;
 * Form überarbeitet [E-387](../../../../docs/ENTSCHEIDUNGEN.md#e-387)).
 *
 * Christian Hunhoff, 30.09.2026: *„wäre es möglich für Kommentare und Beschreibung einen Button
 * für «Lesemodus» bzw. → Neues Tab mit nur dem HTML bzw. Textinhalt hinzufügen. Den je nach
 * Menge des Texts und wenn es auch noch eine Mail ist, sind die Kommentar echt unübersichtlich
 * zu lesen."*
 *
 * ## Kein neues Tab
 *
 * Ein zweiter Reiter bräuchte eine eigene Adresse, eine eigene Rechteprüfung und eine zweite
 * Stelle, an der derselbe Text dargestellt wird. Und er verliert den Zusammenhang: Wer eine
 * Mail liest, will danach **hier** antworten, nicht zurückwandern.
 *
 * Stattdessen eine Ebene über der Aufgabe – breiter, größer, mit Zeilenlänge. Esc schließt.
 *
 * ## Warum ein Knopf und keine andere Grundeinstellung
 *
 * Weil die meisten Kommentare zwei Zeilen haben. Eine Leseansicht für zwei Zeilen ist ein
 * Klick, der nichts ändert; deshalb erscheint der Knopf erst ab {@link LESEMODUS_AB} Zeichen –
 * bei kurzen Texten gibt es ihn gar nicht.
 *
 * ## Der Regler ändert, wie viel man sieht – nicht, wie groß die Karte ist
 *
 * ([E-388](../../../../docs/ENTSCHEIDUNGEN.md#e-388) → [E-391](../../../../docs/ENTSCHEIDUNGEN.md#e-391)
 * → [E-395](../../../../docs/ENTSCHEIDUNGEN.md#e-395), 01./02.10.2026 – drei Anläufe, und die
 * ersten beiden lagen daneben.)
 *
 * Christian Hunhoff hat dreimal dasselbe gesagt und ich habe es zweimal anders gebaut:
 *
 * 1. *„viel zu klein … die aktuelle Breite vom Lesemodus nicht zu gebrauchen"* – ich machte
 *    daraus einen Zoom, bei dem Karte **und** Schrift wuchsen. Der zeigt pro Bildschirm gleich
 *    viel; gewonnen war nichts.
 * 2. Nach Nils' *„der sieht zu wenig"* wurde die Karte breit und die Schrift klein – aber der
 *    Text blieb in einer Spalte von 620 px, damit die Zeile nicht zu lang wird. Christian
 *    schickte ein Bild, auf dem die leere rechte Hälfte rot durchgestrichen ist: *„Breite ist
 *    zwar größer, jedoch hat sich die Textbreite nicht verändert."*
 * 3. **Jetzt nimmt der Text die ganze Fläche.** Kein Deckel mehr.
 *
 * **Die Zeilenlänge war mein Einwand, nicht seiner.** Ein Absatz über 90 Zeichen ist schwerer
 * zu lesen, das bleibt wahr – aber wer lange Mailverläufe überfliegt, sucht Stellen und liest
 * nicht Zeile für Zeile. Dafür ist Fläche das Richtige. Wem die Zeile zu lang wird, **macht die
 * Schrift größer**: Bei gleicher Breite passen dann weniger Zeichen in die Zeile. Der Regler
 * ist damit beides – „mehr sehen" nach unten, „ruhiger lesen" nach oben.
 *
 * Gemerkt wird je Gerät (`useMerker`), nicht am Konto: Am Telefon will man etwas anderes als
 * am 27-Zoll-Schirm.
 *
 * ## Alles ausgeklappt
 *
 * Im Lesemodus stehen Signatur und älterer Verlauf **offen**. Im Verlauf der Aufgabe sind sie
 * Ballast und werden weggeklappt; wer ausdrücklich „lesen" sagt, meint das Ganze – sonst wäre
 * hinter dem Knopf wieder ein Knopf.
 */

/** Ab wann sich eine eigene Leseansicht lohnt – gemessen an der Zeichenzahl des Textes. */
export const LESEMODUS_AB = 600

export function LesemodusKnopf({
  text,
  titel,
  meta,
}: {
  text: string
  titel: string
  /** Eine Zeile Herkunft unter dem Titel – Datum, Quelle. Optional: die Beschreibung hat keine. */
  meta?: string | undefined
}) {
  const [offen, setOffen] = useState(false)
  if (text.length < LESEMODUS_AB) return null
  return (
    <>
      <button
        type="button"
        onClick={() => setOffen(true)}
        title="Groß und ruhig lesen – Esc schließt wieder"
        className="shrink-0 cursor-pointer rounded-md border border-line2 px-1.5 py-[1px] text-[11px] text-muted hover:bg-page hover:text-ink2"
      >
        ⤢ Lesemodus
      </button>
      {offen && (
        <Leseebene text={text} titel={titel} meta={meta} schliessen={() => setOffen(false)} />
      )}
    </>
  )
}

/** Die drei Stufen, klein nach groß – die Reihenfolge ist die der Knöpfe. */
const GROESSEN = ['klein', 'mittel', 'gross'] as const
type Groesse = (typeof GROESSEN)[number]

/**
 * **Die Karte nimmt, was das Fenster hergibt** – für alle Stufen gleich. Sie ist nicht das, was
 * der Regler verstellt; sie ist die Fläche, die Christian gefehlt hat. 1400 px ist die Grenze,
 * an der auch ein sehr breiter Schirm noch einen Rand behält.
 */
const KARTE = 'max-w-[1400px]'

/**
 * **Was eine Stufe ausmacht: die Schriftgröße, und mit ihr die Bildhöhe.**
 *
 * Keine Textbreite mehr (E-395): Der Text läuft über die ganze Karte. Kleiner heißt deshalb
 * mehr Zeichen je Zeile **und** mehr Zeilen – wer eine kürzere Zeile will, geht auf „größer".
 *
 * Die Klassennamen stehen ausgeschrieben da, weil Tailwind den Quelltext liest: aus
 * `text-[${n}px]` entstünde keine Regel.
 */
const MASSE: Record<Groesse, { schrift: string; bild: string }> = {
  klein: { schrift: 'text-[13.5px]', bild: '[&_img]:max-h-[520px]' },
  mittel: { schrift: 'text-[15px]', bild: '[&_img]:max-h-[580px]' },
  gross: { schrift: 'text-[16.5px]', bild: '[&_img]:max-h-[640px]' },
}

function Leseebene({
  text,
  titel,
  meta,
  schliessen,
}: {
  text: string
  titel: string
  meta?: string | undefined
  schliessen: () => void
}) {
  const [groesse, setGroesse] = useMerker<Groesse>('lesemodus-groesse', 'mittel', GROESSEN)
  const stufe = GROESSEN.indexOf(groesse)
  const mass = MASSE[groesse]

  /**
   * **Esc schließt – und nur diese Ebene.** `stopPropagation`, weil darunter das
   * Aufgabenfenster denselben Zuhörer am `window` hat: Ohne ihn klappten beide gleichzeitig zu,
   * und man stünde wieder in der Liste.
   */
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      schliessen()
    }
    window.addEventListener('keydown', taste, true)
    return () => window.removeEventListener('keydown', taste, true)
  }, [schliessen])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Lesemodus: ${titel}`}
      /**
       * **Beginnt unter der Kopfleiste, nicht am Fensterrand** ([E-325](../../../../docs/ENTSCHEIDUNGEN.md#e-325)).
       * Eine Ebene über das ganze Fenster läge unter der klebenden NetCon-Leiste, und auf dem
       * Telefon verschwände das „schließen" dahinter. `lib/unter-leiste.test.ts` wacht darüber –
       * er sucht die Zeichenkette im Quelltext, also auch in einem Kommentar wie diesem.
       *
       * **Dunkler als die Aufgabenebene** (E-387): Darunter liegt kein Tabellenhintergrund,
       * sondern das weiße Aufgabenfenster. Mit 0,38 wie dort stand weiß auf weiß, und die
       * Leseebene sah aus wie ein Kasten auf einem Kasten statt wie eine Seite.
       */
      className="unter-leiste fixed inset-x-0 bottom-0 z-[60] flex flex-col bg-[rgb(23_22_20/0.55)] p-0 sm:p-6 sm:pt-[4vh]"
      onClick={schliessen}
    >
      {/**
       * `max-h-full` und keine `vh`-Angabe: Die Abdunklung ist um die Leiste kürzer als das
       * Fenster, `100%` ist genau ihr Platz. Eine Karte mit `88vh` ragte unten hinaus, und das
       * Ende ihres Scrollbereichs wäre nicht erreichbar (E-325).
       *
       * **Feste Pixel statt `ch`** (E-387): `ch` hängt an der Schrift des *Trägers* – 14 px –,
       * nicht an der des Textes darin. `max-w-[72ch]` ergab eine Karte von 553 px mit gut 60
       * Zeichen Text: schmaler als die Aufgabe, vor der sie stand. Christian Hunhoff, 01.10.:
       * *„ist der Lesemodus viel zu klein … die aktuelle Breite vom Lesemodus nicht zu
       * gebrauchen"*. Die Breite ist seit E-391 für alle Stufen dieselbe – siehe {@link KARTE}.
       */}
      <div
        className={`mx-auto flex max-h-full w-full ${KARTE} flex-col overflow-hidden bg-surface shadow-card max-sm:h-full sm:rounded-card sm:border sm:border-line`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-line px-5 py-2.5 sm:px-10">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[12.5px] font-semibold text-ink2">{titel}</h2>
            {meta && <p className="truncate text-[11px] text-muted">{meta}</p>}
          </div>
          {/**
           * **Zwei Knöpfe und keine drei Beschriftungen.** „kleiner / größer" ist, was man im
           * Moment des Lesens denkt; „klein / mittel / groß" verlangt, die eigene Stufe erst zu
           * suchen. Am Ende der Skala wird der Knopf blass statt zu verschwinden – ein Knopf,
           * der wegspringt, verschiebt den anderen unter dem Finger.
           */}
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setGroesse(GROESSEN[stufe - 1] ?? 'klein')}
              disabled={stufe === 0}
              title="Kleiner – es passt mehr auf den Schirm"
              aria-label="Kleiner"
              className="cursor-pointer rounded-md border border-line2 px-2 py-1 text-[11px] text-muted hover:bg-page hover:text-ink2 disabled:cursor-default disabled:opacity-35 disabled:hover:bg-transparent"
            >
              A−
            </button>
            <button
              type="button"
              onClick={() => setGroesse(GROESSEN[stufe + 1] ?? 'gross')}
              disabled={stufe === GROESSEN.length - 1}
              title="Größer – dafür passt weniger auf den Schirm"
              aria-label="Größer"
              className="cursor-pointer rounded-md border border-line2 px-2 py-1 text-[13px] text-muted hover:bg-page hover:text-ink2 disabled:cursor-default disabled:opacity-35 disabled:hover:bg-transparent"
            >
              A＋
            </button>
          </div>
          {/**
           * **Ein Knopf, der aussieht wie einer.** Vorher stand hier grauer Text in 12 px neben
           * einem 16-px-Absatz – das las sich wie eine Fußnote und nicht wie der Ausgang.
           */}
          <button
            type="button"
            onClick={schliessen}
            className="shrink-0 cursor-pointer rounded-md border border-line2 px-2 py-1 text-[11.5px] text-muted hover:bg-page hover:text-ink2"
          >
            schließen (Esc)
          </button>
        </div>
        {/**
         * **Der ganze Unterschied zum Text in der Aufgabe, und es ist der, auf den es beim
         * Lesen ankommt**: 14 bis 17,5 px statt 12,5, Zeilenabstand 1,75 statt 1,5, Ränder, in
         * denen der Text steht statt an der Kante zu kleben – und Absätze, die sich voneinander
         * absetzen (`p:not(:first-child)` schlägt das `mt-2.5` aus dem Renderer, ohne dem
         * ersten Absatz einen Einzug zu geben).
         *
         * `hyphens-auto` wirkt nur, weil `<html lang="de">` gesetzt ist: ohne Sprache weiß der
         * Browser nicht, wo er trennen darf. Bei deutschen Wörtern in einer 74-Zeichen-Spalte
         * ist das der Unterschied zwischen einem Rand und einer Treppe.
         *
         * `[&_img]` hebt die 420-px-Grenze aus dem Renderer an: Dort ist ein Bild ein Beleg im
         * Verlauf, hier ist es das, was man ansehen will – Christian nennt es als zweiten Grund
         * für den Lesemodus. Eine `vh`-Angabe wäre hier falsch und `unter-leiste.test.ts` fängt
         * sie ab – in einer Abdunklung, die unter der Leiste beginnt, rechnet nichts mehr mit
         * der vollen Fensterhöhe (E-325).
         */}
        <div
          className={`overflow-y-auto overscroll-contain px-5 py-6 ${mass.schrift} leading-[1.75] break-words text-ink hyphens-auto sm:px-10 sm:pt-7 sm:pb-12 ${mass.bild} [&_p:not(:first-child)]:mt-[0.8em]`}
        >
          <Markdown text={text} allesZeigen />
        </div>
      </div>
    </div>
  )
}
